import { Component, type ErrorInfo, type ReactNode } from 'react';

import { ErrorState } from './ErrorState';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Wired to TanStack Query's reset so "Try again" actually refetches. */
  onReset?: () => void;
  fallback?: (error: unknown, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: unknown;
}

/**
 * Catches render-time errors below it. Still a class component — React has no
 * hook equivalent of componentDidCatch.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Deliberately console-only: there is no error-reporting service wired up,
    // and swallowing this silently would make production bugs invisible.
    console.error('Unhandled render error:', error, info.componentStack);
  }

  reset = (): void => {
    this.props.onReset?.();
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;

    if (error !== null) {
      return (
        this.props.fallback?.(error, this.reset) ?? (
          <ErrorState error={error} onRetry={this.reset} title="Something went wrong" />
        )
      );
    }

    return this.props.children;
  }
}
